import { useState } from 'react';
import { SortingState } from '../../types';

/**
 * Hook for managing table sorting state
 *
 * This hook provides functionality for sorting table columns,
 * supporting ascending/descending sort directions and sort field changes.
 *
 * @template T - The type of data rows being sorted
 * @param initialState - Initial sorting configuration (orderBy, order)
 *
 * @returns Object containing:
 *   - sorting: Current sorting state
 *   - handleSort: Function to handle sort changes by property
 *
 * @example
 * ```tsx
 * const { sorting, handleSort } = useSorting({
 *   orderBy: 'name',
 *   order: 'asc'
 * });
 *
 * // Handle sort change (toggles direction if same field)
 * handleSort('email');
 *
 * // Current sort info
 * console.log(sorting?.orderBy, sorting?.order);
 * ```
 */
export const useSorting = <T>(initialState?: SortingState<T>) => {
  const [sorting, setSorting] = useState(initialState);

  const handleSort = (property: keyof T) => {
    const isAsc = sorting?.orderBy === property && sorting?.order === 'asc';
    setSorting({ orderBy: property, order: isAsc ? 'desc' : 'asc' });
  };

  return { sorting, handleSort };
};
